/** @format */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CiSearch } from "react-icons/ci";
import { motion } from "framer-motion";
import Select from "react-select";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AttendanceAdjustment = () => {
  const API_URL = process.env.REACT_APP_FRONTEND_URL;
  const navigate = useNavigate();

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    employeeId: "",
    date: "",
    checkIn: "",
    checkOut: "",
    reason: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  // 🎨 Avatar color class generator
  const avatarBgClass = (seed = "") => {
    const palette = [
      "bg-sky-500",
      "bg-indigo-500",
      "bg-emerald-500",
      "bg-rose-500",
      "bg-amber-500",
      "bg-teal-500",
      "bg-fuchsia-500",
      "bg-cyan-500",
    ];
    let hash = 0;
    for (let i = 0; i < seed.length; i++)
      hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
    return palette[hash % palette.length];
  };

  // 🅰️ Initials from name
  const getInitials = (fullName = "") => {
    const tokens = String(fullName)
      .replace(/[^\p{L}\p{N}\s'-]/gu, "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (tokens.length === 0) return "??";
    if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase();
    const first = tokens[0][0] || "";
    const last = tokens[tokens.length - 1][0] || "";
    return (first + last).toUpperCase();
  };

  // 🔹 Fetch employees
  const fetchEmployees = async (
    page = 1,
    limit = rowsPerPage,
    keyword = ""
  ) => {
    setLoading(true);
    setError("");

    try {
      const query = new URLSearchParams({
        page,
        limit,
      });

      if (keyword && keyword.trim() !== "") query.append("search", keyword);

      const res = await fetch(
        `${API_URL}/v1/hris/employees/employee/all-details?${query}`
      );
      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data?.message || "Failed to fetch employees");
      }

      setEmployees(data.data || []);
      setTotalRecords(data.totalRecords || 0);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error("❌ Fetch error:", err);
      setError(err.message);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Debounce search (400ms)
  const debounceSearch = useCallback(() => {
    const delay = setTimeout(() => {
      setCurrentPage(1);
      fetchEmployees(1, rowsPerPage, search);
    }, 400);
    return () => clearTimeout(delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // 🔹 Initial + search watcher
  useEffect(() => {
    debounceSearch();
  }, [search, debounceSearch]);

  // 🔹 Pagination watcher
  useEffect(() => {
    fetchEmployees(currentPage, rowsPerPage, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // 🔹 Reset when search cleared
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    if (value.trim() === "") {
      setCurrentPage(1);
      fetchEmployees(1, rowsPerPage, "");
    }
  };

  // 🔍 Fetch employee options dynamically
  const fetchEmployeeOptions = async (query) => {
    if (!query || query.trim().length < 2) {
      setEmployeeOptions([]);
      return;
    }

    try {
      setLoadingEmployees(true);
      const res = await fetch(
        `${API_URL}/v1/hris/employees/employee/all-details?search=${encodeURIComponent(query)}`
      );
      const data = await res.json();

      if (data.success && Array.isArray(data.data)) {
        const options = data.data.map((emp) => ({
          value: emp.employee_no,
          label: `${emp.employee_no} — ${emp.employee_fullname} (${emp.department_name || "N/A"})`,
        }));
        setEmployeeOptions(options);
      } else {
        setEmployeeOptions([]);
      }
    } catch (err) {
      console.error("Error fetching employees:", err);
      setEmployeeOptions([]);
    } finally {
      setLoadingEmployees(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.4 }}
    >
      <div className="font-montserrat">
        <p className="text-[25px] my-5">Attendance Adjustment</p>

        <div className="flex items-center gap-3 mb-4">
          <div className="relative w-72">
            <CiSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search (No, Name, Email...)"
              value={search}
              onChange={handleSearchChange}
              className="pl-10 border p-2 rounded w-full focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>


        </div>

        <div className="justify-end flex">
          <button
            onClick={() => setShowModal(true)}
            className="bg-blue-500 p-2 rounded-md text-white hover:bg-blue-600"
          >
            Add Manual Attendance
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto bg-white shadow p-2 rounded-xl">
          <table className="w-full text-sm text-left">
            <thead className="bg-white text-gray-600 border-b border-t">
              <tr>
                <th className="px-6 py-4">Employee No</th>
                <th className="px-6 py-4">Employee Name</th>
                <th className="px-6 py-4">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 border-t border-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="3" className="text-center py-4 text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="3" className="text-center py-4 text-red-500">
                    Error: {error}
                  </td>
                </tr>
              ) : employees.length > 0 ? (
                employees.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50 transition">
                    <td className="p-3 text-blue-600">{row.employee_no}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs ${avatarBgClass(
                            row.employee_fullname || row.employee_no
                          )}`}
                          title={row.employee_fullname}
                        >
                          {getInitials(row.employee_fullname || row.employee_no)}
                        </div>
                        <div>
                          <div className="font-semibold">
                            {row.employee_fullname}
                          </div>
                          <div className="text-xs text-gray-500">
                            EMP ID: {row.employee_no}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() =>
                          navigate(`/adjustment-each-employee/${row.employee_no}`)
                        }
                        className="bg-blue-500 text-white hover:bg-blue-600 px-3 py-1.5 rounded"
                      >
                        View Attendance
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="text-center py-4 text-gray-500">
                    No data found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center mt-4">
          <span className="text-sm text-gray-700">
            Showing {totalRecords > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0}{" "}
            to {Math.min(currentPage * rowsPerPage, totalRecords)} of{" "}
            {totalRecords} results
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1 || loading}
              className="px-4 py-2 text-sm bg-white border rounded-md disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm">
              Page {currentPage} of {totalPages > 0 ? totalPages : 1}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0 || loading}
              className="px-4 py-2 text-sm bg-white border rounded-md disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
        {/* 🧾 Manual Attendance Modal */}
        {/* 🧾 Manual Attendance Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 relative">
              {/* Close Button */}
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>

              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Add Manual Attendance
              </h2>

              <div className="space-y-4">
                {/* Select Employee (searchable) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Employee
                  </label>
                  <Select
                    placeholder="Search employee by name or number..."
                    isClearable
                    isLoading={loadingEmployees}
                    options={employeeOptions}
                    onInputChange={(value) => {
                      setEmployeeSearch(value);
                      fetchEmployeeOptions(value); // fetch when typing
                    }}
                    onChange={(opt) =>
                      setFormData({ ...formData, employeeId: opt ? opt.value : "" })
                    }
                    value={
                      formData.employeeId
                        ? {
                          value: formData.employeeId,
                          label:
                            employeeOptions.find((o) => o.value === formData.employeeId)
                              ?.label || formData.employeeId,
                        }
                        : null
                    }
                    className="basic-single"
                    classNamePrefix="select"
                    noOptionsMessage={() =>
                      employeeSearch.length < 2
                        ? "Type 2+ characters to search"
                        : "No employees found"
                    }
                  />
                </div>


                {/* Date & Time (combined) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    max={new Date().toISOString().slice(0, 16)} // prevent future
                    value={formData.datetime}
                    onChange={(e) =>
                      setFormData({ ...formData, datetime: e.target.value })
                    }
                    className="border w-full rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>

                {/* Reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    rows="3"
                    placeholder="Enter reason..."
                    value={formData.reason}
                    onChange={(e) =>
                      setFormData({ ...formData, reason: e.target.value })
                    }
                    className="border w-full rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                    required
                  ></textarea>
                </div>
              </div>

              {/* Footer Buttons */}
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  disabled={
                    submitting ||
                    !formData.employeeId ||
                    !formData.datetime ||
                    !formData.reason
                  }
                  onClick={async () => {
                    setSubmitting(true);
                    try {
                      const res = await fetch(`${API_URL}/v1/hris/attendence/manual`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                          employeeid: formData.employeeId,
                          datetime: formData.datetime.replace("T", " ") + ":00",
                          reason: formData.reason,
                        }),
                      });

                      const data = await res.json();
                      if (res.ok && data.success) {
                        toast.success("✅ Manual attendance added successfully!");
                        setShowModal(false);
                        setFormData({ employeeId: "", datetime: "", reason: "" });
                      } else {
                        toast.error(data.message || "❌ Failed to add attendance record");
                      }
                    } catch (error) {
                      console.error("Error submitting manual attendance:", error);
                      toast.error("❌ Failed to submit manual attendance.");
                    } finally {
                      setSubmitting(false);
                    }
                  }}

                  className={`px-4 py-2 rounded-md text-white ${submitting
                    ? "bg-blue-300 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                    }`}
                >
                  {submitting ? "Saving..." : "Save Attendance"}
                </button>
              </div>
            </div>
          </div>
        )}

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnHover
          draggable
          theme="colored"
        />

      </div>
    </motion.div>
  );
};

export default AttendanceAdjustment;
